"use client";

import { useState } from "react";
import Link from "@/components/ui/LocaleLink";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useNextParam } from "./useNextParam";
import LastUsedTag, { useLastSignIn } from "@/components/auth/LastUsedTag";
import { Check, Eye, EyeSlash } from "@phosphor-icons/react/dist/ssr";
import { createClient } from "@/lib/supabase/client";
import { PASSWORD_RULES, passwordValid } from "@/lib/password-rules";
import { isValidEmail } from "@/lib/validations";
import { resolveAuthError } from "@/lib/auth-errors";
import { captchaOptions } from "@/lib/turnstile";
import { useTurnstile } from "./useTurnstile";
import { BusyLabel } from "@/components/ui/Spinner";

// ── Component ───────────────────────────────────────────────────────────────

export default function SignupForm() {
  const t = useTranslations("auth");
  const next = useNextParam();
  const last = useLastSignIn();
  const tErr = useTranslations("authErrors");
  const router = useRouter();
  const captcha = useTurnstile("signup");
  const [pending,         setPending]         = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [password,        setPassword]        = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword,    setShowPassword]    = useState(false);
  const [fieldErrors,     setFieldErrors]     = useState<Set<string>>(new Set());

  // Show the checklist if the field is focused OR if the user has typed anything
  const showRules = passwordFocused || password.length > 0;

  const normalInput = "rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus-visible:border-gold focus-visible:outline-none";
  const errInput    = "rounded-control border border-error-line bg-red-50/40 px-13 py-8 text-sm text-navy placeholder:text-gray-500 transition-colors";

  // ── Email / password sign-up ─────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const els   = e.currentTarget.elements;
    const nameVal  = (els.namedItem("name")  as HTMLInputElement).value.trim();
    const emailVal = (els.namedItem("email") as HTMLInputElement).value.trim();

    const emptyFields: string[] = [];
    if (!nameVal)  emptyFields.push("name");
    if (!emailVal) emptyFields.push("email");
    if (!password) emptyFields.push("password");
    if (emptyFields.length) {
      setFieldErrors(new Set(emptyFields));
      setError(t("fillAllFields"));
      return;
    }
    setFieldErrors(new Set());

    if (!passwordValid(password)) {
      setFieldErrors(new Set(["password"]));
      setError(t("passwordRequirementsError"));
      return;
    }

    const blocked = captcha.guard();
    if (blocked) {
      setError(tErr(blocked));
      return;
    }

    setPending(true);

    // Compose the auth-callback URL once — same redirect target for both
    // email-confirmation and OAuth. `?next=` lets the callback route return
    // the user to whatever page they were on before signup (e.g. a pro
    // profile they were about to contact).
    const callback = `${window.location.origin}/auth/callback`;
    const redirectTo = next ? `${callback}?next=${encodeURIComponent(next)}` : callback;

    const supabase = createClient();
    const { error: signUpError } = await supabase.auth.signUp({
      email: emailVal,
      password,
      options: {
        data: { full_name: nameVal },
        emailRedirectTo: redirectTo,
        ...captchaOptions(captcha.token),
      },
    });

    setPending(false);
    // The token is spent either way; a retry needs a fresh one.
    captcha.reset();

    if (signUpError) {
      const r = resolveAuthError(signUpError);
      setError("key" in r ? tErr(r.key) : r.message);
      return;
    }

    router.push(`/verify?email=${encodeURIComponent(emailVal)}`);
  }

  // ── OAuth sign-up / sign-in ──────────────────────────────────────────────
  async function handleOAuth(provider: "google") {
    const supabase = createClient();
    const callback = `${window.location.origin}/auth/callback`;
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: next ? `${callback}?next=${encodeURIComponent(next)}` : callback,
      },
    });
  }

  return (
    <>
      {/* ── Social auth ── */}
      <div className="mt-21 space-y-8">
        <button
          type="button"
          onClick={() => handleOAuth("google")}
          className={`relative flex w-full items-center justify-center gap-8 rounded-control border ${last === "google" ? "border-navy/55" : "border-navy/20"} py-[10px] text-sm font-semibold text-navy transition-colors hover:border-navy/50 hover:bg-navy/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold`}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icon_google.svg" alt="" width={16} height={16} aria-hidden="true" />
          {t("continueGoogle")}
          {last === "google" && <LastUsedTag placement="corner" />}
        </button>
      </div>

      {/* ── Divider ── */}
      <div className="relative my-21 flex items-center" aria-hidden="true">
        <div className="flex-1 border-t border-navy/10" />
        <span className="mx-13 text-xs uppercase tracking-[0.08em] text-gray-500">{t("or")}</span>
        <div className="flex-1 border-t border-navy/10" />
      </div>

      {/* ── Email / password form ── */}
      <form
        onSubmit={handleSubmit}
        className="space-y-13"
        noValidate
        aria-label={t("signupFormAria")}
      >
        {error && (
          <p role="alert" className="rounded-control bg-red-50 px-13 py-8 text-sm text-red-700">
            {error}
          </p>
        )}

        {/* Full name */}
        <div className="flex flex-col gap-3">
          <label
            htmlFor="name"
            className="text-xs font-semibold uppercase tracking-wider text-navy"
          >
            {t("fullNameLabel")} <span aria-hidden="true">*</span>
            <span className="sr-only">{t("required")}</span>
          </label>
          <input
            id="name"
            type="text"
            name="name"
            required
            aria-required="true"
            autoComplete="name"
            placeholder={t("fullNamePlaceholder")}
            className={fieldErrors.has("name") ? errInput : normalInput}
            onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("name"); return n; })}
          />
        </div>

        {/* Email */}
        <div className="flex flex-col gap-3">
          <label
            htmlFor="email"
            className="text-xs font-semibold uppercase tracking-wider text-navy"
          >
            {t("emailLabel")} <span aria-hidden="true">*</span>
            <span className="sr-only">{t("required")}</span>
          </label>
          <input
            id="email"
            type="email"
            name="email"
            required
            aria-required="true"
            autoComplete="email"
            placeholder="you@example.com"
            className={fieldErrors.has("email") ? errInput : normalInput}
            onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("email"); return n; })}
            onBlur={(e) => { const v = e.target.value.trim(); if (v && !isValidEmail(v)) setFieldErrors(prev => new Set([...prev, "email"])); }}
          />
        </div>

        {/* Password + live rules */}
        <div className="flex flex-col gap-3">
          <label
            htmlFor="password"
            className="text-xs font-semibold uppercase tracking-wider text-navy"
          >
            {t("passwordLabel")} <span aria-hidden="true">*</span>
            <span className="sr-only">{t("required")}</span>
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              required
              aria-required="true"
              autoComplete="new-password"
              placeholder={t("passwordPlaceholder")}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => { const n = new Set(prev); n.delete("password"); return n; }); }}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              aria-describedby={showRules ? "password-rules" : undefined}
              className={`w-full ${fieldErrors.has("password") ? errInput : normalInput} pr-[38px]`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("hidePassword") : t("showPassword")}
              className="absolute inset-y-0 right-0 flex items-center px-13 text-gray-500 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              {showPassword
                ? <EyeSlash size={16} aria-hidden="true" />
                : <Eye size={16} aria-hidden="true" />
              }
            </button>
          </div>

          {/* Rules checklist — shown on focus or when user has typed */}
          {showRules && (
            <ul
              id="password-rules"
              role="list"
              aria-label={t("passwordRulesAria")}
              className="mt-3 space-y-[5px]"
            >
              {PASSWORD_RULES.map((rule) => {
                const met = rule.test(password);
                return (
                  <li
                    key={rule.id}
                    className={[
                      "flex items-center gap-8 text-[11px] transition-colors",
                      met ? "text-green-strong" : "text-gray-500",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border transition-colors",
                        met
                          ? "border-green-strong bg-green-strong"
                          : "border-gray-300 bg-transparent",
                      ].join(" ")}
                      aria-hidden="true"
                    >
                      {met && <Check size={8} weight="bold" className="text-white" />}
                    </span>
                    <span>{t(rule.labelKey)}</span>
                    <span className="sr-only">{met ? t("ruleMet") : t("ruleNotMet")}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="text-xs leading-relaxed text-gray-600">
          {t.rich("agreeTerms", {
            terms: (chunks) => (
              <Link
                href="/terms"
                className="text-navy underline underline-offset-2 hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
              >
                {chunks}
              </Link>
            ),
            privacy: (chunks) => (
              <Link
                href="/privacy"
                className="text-navy underline underline-offset-2 hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
              >
                {chunks}
              </Link>
            ),
          })}
        </p>

        {captcha.widget}

        <button
          type="submit"
          disabled={pending}
          aria-disabled={pending}
          className="mt-8 w-full rounded-control bg-gold py-13 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          <BusyLabel busy={pending}>{pending ? t("creatingAccount") : t("createAccountButton")}</BusyLabel>
        </button>
      </form>
    </>
  );
}
